<script setup>
/**
 * Overlay visual de tour guiado — genérico, reaproveitável em qualquer
 * módulo (extraído de PipelineTour.vue). Recebe tudo via props/emits, não
 * conhece nada do módulo que o está usando. Ver useGuidedTour.js.
 */
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  isActive: { type: Boolean, default: false },
  currentStep: { type: Object, default: null },
  currentStepIndex: { type: Number, default: 0 },
  totalSteps: { type: Number, default: 0 },
  progress: { type: Number, default: 0 },
  isFirstStep: { type: Boolean, default: true },
  isLastStep: { type: Boolean, default: false },
  targetRect: { type: Object, default: null },
});

const emit = defineEmits(['next', 'prev', 'skip', 'complete']);

const { t } = useI18n();

const isTargetMissing = computed(
  () =>
    !!props.currentStep?.target &&
    props.currentStep.position !== 'center' &&
    !props.targetRect
);

// Com destaque, quem escurece é o FURO (a sombra do `__highlight`); o véu
// fica transparente, só para o clique fora pular. Antes os dois escureciam
// e o véu ainda desfocava a página inteira — o alvo saía borrado justamente
// no passo que existe para mostrá-lo (visto no "Me mostra" do Lumos,
// 08/10/2026).
const temFuro = computed(
  () =>
    !!props.currentStep?.highlight &&
    !isTargetMissing.value &&
    !!props.targetRect
);

const tooltipStyle = computed(() => {
  const step = props.currentStep;
  if (!step) return {};

  if (step.position === 'center' || !props.targetRect) {
    return {
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
    };
  }

  const rect = props.targetRect;
  const padding = 16;
  const tooltipWidth = 320;
  const tooltipHeight = 200;

  let top;
  let left;

  switch (step.position) {
    case 'top':
      top = rect.top - tooltipHeight - padding;
      left = rect.left + rect.width / 2 - tooltipWidth / 2;
      break;
    case 'bottom':
      top = rect.bottom + padding;
      left = rect.left + rect.width / 2 - tooltipWidth / 2;
      break;
    case 'left':
      top = rect.top + rect.height / 2 - tooltipHeight / 2;
      left = rect.left - tooltipWidth - padding;
      break;
    case 'right':
      top = rect.top + rect.height / 2 - tooltipHeight / 2;
      left = rect.right + padding;
      break;
    default:
      top = rect.bottom + padding;
      left = rect.left;
  }

  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  if (left < padding) left = padding;
  if (left + tooltipWidth > viewportWidth - padding) {
    left = viewportWidth - tooltipWidth - padding;
  }
  if (top < padding) top = padding;
  if (top + tooltipHeight > viewportHeight - padding) {
    top = viewportHeight - tooltipHeight - padding;
  }

  return { position: 'fixed', top: `${top}px`, left: `${left}px` };
});

const highlightStyle = computed(() => {
  if (!props.targetRect || !props.currentStep?.highlight) {
    return { display: 'none' };
  }

  const rect = props.targetRect;
  const padding = 8;

  return {
    position: 'fixed',
    top: `${rect.top - padding}px`,
    left: `${rect.left - padding}px`,
    width: `${rect.width + padding * 2}px`,
    height: `${rect.height + padding * 2}px`,
  };
});

const handleNext = () => {
  if (props.isLastStep) emit('complete');
  else emit('next');
};
const handlePrev = () => emit('prev');
const handleSkip = () => emit('skip');
</script>

<template>
  <Teleport to="body">
    <Transition name="tour-fade">
      <div v-if="isActive" class="guided-tour">
        <div
          class="guided-tour__overlay"
          :class="{ 'guided-tour__overlay--furo': temFuro }"
          @click="handleSkip"
        />

        <div
          v-if="targetRect && currentStep?.highlight"
          class="guided-tour__highlight"
          :style="highlightStyle"
        />

        <div class="guided-tour__tooltip" :style="tooltipStyle">
          <div class="guided-tour__progress">
            <div
              class="guided-tour__progress-bar"
              :style="{ width: `${progress}%` }"
            />
          </div>

          <div class="guided-tour__content">
            <div class="guided-tour__step-indicator">
              {{ currentStepIndex + 1 }} / {{ totalSteps }}
            </div>
            <h3 class="guided-tour__title">{{ currentStep?.title }}</h3>
            <p class="guided-tour__description">{{ currentStep?.content }}</p>
          </div>

          <div class="guided-tour__actions">
            <button
              v-if="currentStep?.showSkip !== false && !isLastStep"
              class="guided-tour__skip"
              @click="handleSkip"
            >
              {{ t('GENERAL_TOUR.SKIP') }}
            </button>

            <div class="guided-tour__nav">
              <Button
                v-if="!isFirstStep"
                variant="faded"
                color="slate"
                size="sm"
                @click="handlePrev"
              >
                {{ t('GENERAL_TOUR.PREVIOUS') }}
              </Button>
              <Button
                variant="solid"
                color="blue"
                size="sm"
                @click="handleNext"
              >
                {{
                  isLastStep ? t('GENERAL_TOUR.FINISH') : t('GENERAL_TOUR.NEXT')
                }}
              </Button>
            </div>
          </div>

          <div class="guided-tour__keyboard-hint">
            <kbd>{{ '←' }}</kbd> <kbd>{{ '→' }}</kbd>
            {{ t('GENERAL_TOUR.KEYBOARD_NAV') }}
            <span class="mx-2">{{ '|' }}</span>
            <kbd>{{ 'Esc' }}</kbd> {{ t('GENERAL_TOUR.TO_SKIP') }}
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.guided-tour {
  position: fixed;
  inset: 0;
  z-index: 9300;
  pointer-events: none;
}

.guided-tour__overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(2px);
  pointer-events: auto;
  cursor: pointer;
}
.dark .guided-tour__overlay {
  background: rgba(0, 0, 0, 0.75);
}

.guided-tour__overlay.guided-tour__overlay--furo {
  background: transparent;
  backdrop-filter: none;
}

.guided-tour__highlight {
  border-radius: 8px;
  box-shadow:
    0 0 0 4px rgba(31, 111, 235, 0.55),
    0 0 0 9999px rgba(0, 0, 0, 0.6);
  z-index: 9310;
  pointer-events: none;
  transition:
    top var(--mov-2) var(--mov-curva),
    left var(--mov-2) var(--mov-curva),
    width var(--mov-2) var(--mov-curva),
    height var(--mov-2) var(--mov-curva);
}
.dark .guided-tour__highlight {
  box-shadow:
    0 0 0 4px rgba(96, 165, 250, 0.5),
    0 0 0 9999px rgba(0, 0, 0, 0.75);
}

.guided-tour__tooltip {
  width: min(320px, calc(100vw - 32px));
  max-width: calc(100vw - 32px);
  background: white;
  border-radius: 12px;
  box-shadow:
    0 20px 25px -5px rgb(0 0 0 / 0.1),
    0 8px 10px -6px rgb(0 0 0 / 0.1);
  z-index: 9320;
  pointer-events: auto;
  overflow: hidden;
  animation: tourTooltipEnter var(--animation-duration-moderate, 200ms)
    var(--animation-ease-enter, ease-out);
}
.dark .guided-tour__tooltip {
  background: rgb(var(--slate-3));
  box-shadow:
    0 20px 25px -5px rgb(0 0 0 / 0.3),
    0 8px 10px -6px rgb(0 0 0 / 0.2);
}

@keyframes tourTooltipEnter {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.guided-tour__progress {
  height: 3px;
  background: rgb(var(--slate-4));
}
.dark .guided-tour__progress {
  background: rgb(var(--slate-5));
}
.guided-tour__progress-bar {
  height: 100%;
  background: linear-gradient(90deg, rgb(var(--blue-9)), rgb(var(--blue-9)));
  transition: width var(--animation-duration-slow, 250ms)
    var(--animation-ease-default, ease);
}

.guided-tour__content {
  padding: 20px;
}
.guided-tour__step-indicator {
  font-size: 12px;
  font-weight: 500;
  color: rgb(var(--slate-11));
  margin-bottom: 8px;
}
.dark .guided-tour__step-indicator {
  color: rgb(var(--slate-10));
}
/* slate-12: era slate-3, quase branco sobre o balão branco (1,1:1). */
.guided-tour__title {
  font-size: 18px;
  font-weight: 600;
  color: rgb(var(--slate-12));
  margin: 0 0 8px 0;
  line-height: 1.3;
}
.dark .guided-tour__title {
  color: rgb(var(--slate-12));
}
.guided-tour__description {
  font-size: 14px;
  color: rgb(var(--slate-11));
  margin: 0;
  line-height: 1.5;
}
.dark .guided-tour__description {
  color: rgb(var(--slate-10));
}

.guided-tour__actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  border-top: 1px solid rgb(var(--slate-4));
  background: rgb(var(--slate-2));
}
.dark .guided-tour__actions {
  border-top-color: rgb(var(--slate-5));
  background: rgb(var(--slate-2));
}

.guided-tour__skip {
  font-size: 13px;
  color: rgb(var(--slate-11));
  background: none;
  border: none;
  padding: 4px 8px;
  cursor: pointer;
  border-radius: 4px;
  transition:
    color var(--mov-1) var(--mov-curva),
    background-color var(--mov-1) var(--mov-curva);
}
.guided-tour__skip:hover {
  color: rgb(var(--slate-11));
  background: rgb(var(--slate-4));
}
.dark .guided-tour__skip {
  color: rgb(var(--slate-10));
}
.dark .guided-tour__skip:hover {
  color: rgb(var(--slate-6));
  background: rgb(var(--slate-5));
}

.guided-tour__nav {
  display: flex;
  gap: 8px;
}

.guided-tour__keyboard-hint {
  font-size: 11px;
  color: rgb(var(--slate-10));
  text-align: center;
  padding: 8px 20px 12px;
  background: rgb(var(--slate-2));
}
.dark .guided-tour__keyboard-hint {
  color: rgb(var(--slate-11));
  background: rgb(var(--slate-2));
}
.guided-tour__keyboard-hint kbd {
  display: inline-block;
  padding: 2px 6px;
  font-size: 10px;
  font-family: inherit;
  background: rgb(var(--slate-4));
  border-radius: 4px;
  border: 1px solid rgb(var(--slate-6));
  margin: 0 2px;
}
.dark .guided-tour__keyboard-hint kbd {
  background: rgb(var(--slate-5));
  border-color: rgb(var(--slate-11));
}

.tour-fade-enter-active,
.tour-fade-leave-active {
  transition: opacity var(--animation-duration-moderate, 200ms)
    var(--animation-ease-default, ease);
}
.tour-fade-enter-from,
.tour-fade-leave-to {
  opacity: 0;
}

@media (max-width: 480px) {
  .guided-tour__content {
    padding: 16px;
  }
  .guided-tour__actions {
    padding: 10px 16px;
  }
  .guided-tour__keyboard-hint {
    padding: 6px 16px 10px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .guided-tour__tooltip {
    animation: none;
  }
  .guided-tour__highlight,
  .guided-tour__progress-bar {
    transition: none;
  }
  .tour-fade-enter-active,
  .tour-fade-leave-active {
    transition: none;
  }
}
</style>
